import get from 'lodash/get';
import { useMemo } from 'react';

import {
   IsCurrentDoctorPsychiatristAndDiagnosisMentalDiseaseQuery,
   MedicalLeave,
   MedicalLeaveDraftQuery,
   MedicalLeavePayer,
} from '@dreryk/egabinet-ezla-frontend-api';
import { Icd10Entry } from '@dreryk/egabinet-icd-dictionary-published-language-api';

import { paginations } from '@dreryk/command-query-ws-client';
import { useCqrsFindFirst, useCqrsGet, UseCqrsQueryResult } from '@dreryk/command-query-react-common';
import { Choice } from '@dreryk/watermelon';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DEFAULT_INSURANCE_TYPE } from '../common/common.constants';
import { LeaveAfterTerminationOfEmploymentRecord } from '../common/common.types';
import { createNewDateRange, mapPatientAddressToMedicalLeaveFormAddress } from '../common/common.utils';

import { getMedicalLeaveFormInitialValues } from './medicalLeave.constants';
import { MedicalLeaveDraft } from './medicalLeave.types';

const pagination = paginations.first<MedicalLeave | null>();

const useMedicalLeaveDraftQuery = (visitId: number): UseCqrsQueryResult<MedicalLeave | null> => {
   return useCqrsFindFirst(() => {
      const query = new MedicalLeaveDraftQuery();
      query.visitId = visitId;
      return query;
   }, pagination);
};

export const findMedicalLeaveDiagnosis = (medicalLeave?: MedicalLeave): Icd10Entry | null =>
   get(medicalLeave, 'diagnosis', null);

const useMedicalLeaveDraft = (medicalLeave?: MedicalLeave | null): MedicalLeaveDraft | undefined => {
   const insuranceType = medicalLeave?.insuranceType || DEFAULT_INSURANCE_TYPE;

   return useMemo<MedicalLeaveDraft | undefined>(() => {
      if (!medicalLeave) {
         return undefined;
      }
      const diagnosis = findMedicalLeaveDiagnosis(medicalLeave);
      const startDate = medicalLeave?.dateFrom ?? null;
      const endDate = medicalLeave?.dateTo ?? null;
      const { address } = medicalLeave ?? {};
      return {
         ...getMedicalLeaveFormInitialValues(),
         ...(medicalLeave || {}),
         id: medicalLeave.id,
         externalId: medicalLeave.externalId,
         // TODO DRWEB-4260 use Range from backend
         leaveDateRange: createNewDateRange(startDate, endDate),
         diagnosis: diagnosis,
         insuranceType: insuranceType,
         leaveAfterTerminationOfEmployment: (medicalLeave?.payers || []).reduce(
            (acc: LeaveAfterTerminationOfEmploymentRecord, payer: MedicalLeavePayer) => {
               const identificationNumber = payer.identificationNumber?.identificationNumber;
               if (!identificationNumber) {
                  return acc;
               }

               return {
                  ...acc,
                  [identificationNumber]: Boolean(payer.afterTerminationOfEmployment),
               };
            },
            {}
         ),
         payers: medicalLeave?.payers ?? [],
         address: address ? mapPatientAddressToMedicalLeaveFormAddress(address) : null,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [medicalLeave]);
};

type UseMedicalLeaveReasonIgnoredOptions = {
   diagnosis: Icd10Entry | null;
};

const useMedicalLeaveReasonIgnored = (options: UseMedicalLeaveReasonIgnoredOptions): boolean => {
   const diagnosisIcd10Code = options.diagnosis?.getCode().asString() ?? null;
   const { data: currentDoctorPsychiatristAndDiagnosisMentalDisease } = useCqrsGet<boolean>(
      () => {
         const query = new IsCurrentDoctorPsychiatristAndDiagnosisMentalDiseaseQuery();
         query.diagnosisIcd10Code = Choice.withUseDefaultIfNull(diagnosisIcd10Code);
         return query;
      },
      {
         enabled: diagnosisIcd10Code !== null,
      }
   );

   return diagnosisIcd10Code !== null && currentDoctorPsychiatristAndDiagnosisMentalDisease === true;
};

export { useMedicalLeaveDraft, useMedicalLeaveDraftQuery, useMedicalLeaveReasonIgnored };
